import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  AlertCircle,
  ArrowRight,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";

const Register = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const token = searchParams.get("token");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (!decoded?.email || !decoded?.exp || decoded.exp < currentTime) {
        window.alert(t?.linkExpired || "Ce lien d'inscription a expiré.");
        navigate("/login", { replace: true });
        return;
      }

      setEmail(decoded.email);
    } catch (error) {
      console.error("Invalid registration token:", error);
      navigate("/login", { replace: true });
    } finally {
      setCheckingToken(false);
    }
  }, [token, navigate, t]);

  const requirements = useMemo(
    () => [
      { label: "8 caractères minimum", valid: password.length >= 8 },
      { label: "Une lettre majuscule", valid: /[A-Z]/.test(password) },
      { label: "Une lettre minuscule", valid: /[a-z]/.test(password) },
      { label: "Un chiffre", valid: /\d/.test(password) },
    ],
    [password]
  );

  const passwordIsValid = requirements.every((item) => item.valid);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setMessageType("");

    if (!passwordIsValid) {
      setMessage("Le mot de passe ne respecte pas encore tous les critères.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email,
          password,
          token,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessage(
          `${t?.errorLabel || "Erreur"}: ${
            data.message || t?.registrationFailed || "Inscription impossible"
          }`
        );
        setMessageType("error");
        return;
      }

      setMessage(t?.accountCreatedSuccess || "Compte créé avec succès.");
      setMessageType("success");
      window.setTimeout(() => navigate("/login", { replace: true }), 1000);
    } catch (error) {
      console.error("Registration error:", error);
      setMessage(
        t?.serverConnectionError ||
          "Impossible de contacter le serveur d'inscription."
      );
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  if (checkingToken) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 text-sm font-bold text-slate-700 shadow-sm">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          Vérification de votre invitation...
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-12 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-100";

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto grid min-h-[calc(100vh-9rem)] max-w-6xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl shadow-slate-200/70 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-blue-900 to-slate-950 p-8 text-white sm:p-10 lg:p-12">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />

          <div className="relative flex h-full flex-col">
            <Link to="/" className="inline-flex items-center gap-3 self-start">
              <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-lg">
                <img
                  src="/logo.jpeg"
                  alt="CertiLink"
                  className="h-full w-full object-contain p-1"
                />
              </span>
              <span className="text-xl font-black">CertiLink</span>
            </Link>

            <div className="my-auto py-12">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3.5 py-2 text-xs font-bold text-cyan-200 ring-1 ring-white/10">
                <Sparkles className="h-4 w-4" />
                Invitation vérifiée
              </div>
              <h1 className="mt-6 text-4xl font-black leading-tight tracking-tight">
                Finalisez votre accès à CertiLink.
              </h1>
              <p className="mt-5 text-sm leading-7 text-blue-100">
                Votre adresse email provient de l'invitation sécurisée. Il ne vous
                reste qu'à renseigner votre nom et créer un mot de passe robuste.
              </p>

              <div className="mt-9 space-y-4">
                {[
                  [ShieldCheck, "Lien d'invitation contrôlé"],
                  [LockKeyhole, "Adresse email verrouillée"],
                  [CheckCircle2, "Accès activé après validation"],
                ].map(([Icon, label]) => (
                  <div key={label} className="flex items-center gap-3 text-sm text-blue-50">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-cyan-300">
                      <Icon className="h-4 w-4" />
                    </span>
                    {label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="flex items-center p-6 sm:p-10 lg:p-12">
          <div className="mx-auto w-full max-w-lg">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
              Création du compte
            </p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950">
              {t?.finalizeRegistration || "Finaliser l'inscription"}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {t?.completeSetupSecurely ||
                "Complétez vos informations pour sécuriser votre accès."}
            </p>

            {message && (
              <div
                className={`mt-6 flex items-start gap-3 rounded-2xl border p-4 text-sm font-semibold ${
                  messageType === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`}
              >
                {messageType === "success" ? (
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                ) : (
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                )}
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-7 space-y-5">
              <label className="block text-sm font-bold text-slate-700">
                {t?.email || "Adresse email"}
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className={`${inputClass} cursor-not-allowed bg-slate-100 pr-12 text-slate-500`}
                  />
                  <LockKeyhole className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
                </div>
                <span className="mt-2 block text-xs font-semibold text-emerald-700">
                  {t?.verifiedLocked || "Vérifiée et non modifiable"}
                </span>
              </label>

              <label className="block text-sm font-bold text-slate-700">
                {t?.name || "Nom complet"}
                <div className="relative mt-2">
                  <User className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={t?.enterName || "Votre nom complet"}
                    required
                    autoComplete="name"
                    className={inputClass}
                  />
                </div>
              </label>

              <label className="block text-sm font-bold text-slate-700">
                {t?.createPassword || "Créer un mot de passe"}
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={t?.chooseStrongPassword || "Mot de passe sécurisé"}
                    required
                    autoComplete="new-password"
                    className={inputClass}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </label>

              <div className="grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
                {requirements.map((requirement) => (
                  <div
                    key={requirement.label}
                    className={`flex items-center gap-2 text-xs font-bold ${
                      requirement.valid ? "text-emerald-700" : "text-slate-500"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full ${
                        requirement.valid
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-200 text-slate-400"
                      }`}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    {requirement.label}
                  </div>
                ))}
              </div>

              <button
                type="submit"
                disabled={isLoading || !name.trim() || !passwordIsValid}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-3.5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <ArrowRight className="h-5 w-5" />
                )}
                {isLoading
                  ? t?.creatingAccount || "Création du compte..."
                  : t?.completeRegistration || "Terminer l'inscription"}
              </button>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Register;
